import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import BookLocationHeader from "../../common/util/BookLocationHeader";
import TenantLogo from "../../../layout/components/TenantLogo";
import BookLocationNavigateNextAction from "../../common/util/BookLocationNavigateNextAction";
import BookLocationNavigateBackAction from "../../common/util/BookLocationNavigateBackAction";
import GeneralDialog from "../../../common/GeneralDialog";
import { Box } from "@mui/material";
import { baseStore, RootState } from "../../../../redux/store";
import { useFetchGroups } from "../../../../redux/flows/fetchGroupsFlow";
import {
  bookingCreationCompleted,
  bookingFlowChanged,
  navigatedToNextStep,
  setUseCase,
  dateSelectedClear,
  durationSelectedClear,
  setVerifyPinResponseData,
  setStep,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { styled } from "@mui/system";
import { useCreateNewReservation } from "../../../../redux/flows/createParcelCustomerReservationFlow";
import routes from "../../../../routes/routes";
import Header from "../../../layout/components/Header";
import CloseIcon from "../../../../components/icons/CloseIcon";
import CustomButton from "../../../layout/components/CustomButton";
import useServiceStorage from "../../../../hooks/useServiceStorage";
import { ServiceTypeSelectorOption } from "../../../../types/order";
import PinVerification, { PinVerificationRef } from "./PinVerification";
import LockerSelection from "./LockerSelection";
import LockerOpen from "./LockerOpen";
import SuccessMessage from "./SuccessMessage";
import LoadingStep from "./LoadingStep";
import { reservationsApiSlice } from "../../../../api/reservations/reservationsApiSlice";
import { enqueueNotification } from "../../../../redux/slice/notification/notificationSlice";
import { VerifyPinResponseData } from "../../../../types/bookingCreation";
import NoLockerAvailable from "./NoLockerAvailable";

export enum BookingCreationStep {
  pinVerification,
  lockSelection,
  openLock,
  success,
  lockerUnavailability,
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)}px + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  width: "calc(100% - 42px)",
  maxWidth: 1000,
  margin: "0 auto",
  paddingBottom: theme.spacing(5),
  // position: 'fixed',
  bottom: 10,
  left: 0,
  right: 0,
  [theme.breakpoints.up("lg")]: {
    paddingBottom: theme.spacing(5),
  },
}));

export const LogoBox = styled(Box)(({ theme }) => ({
  width: "150px",
  marginTop: theme.spacing(1),
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
}));

export const CancelButton = styled(Box)({
  boxSizing: "border-box",
  marginLeft: "auto",
});

interface BookLocationDialogProps {
  returnPage: string;
}

const BookLocationPage = (props: BookLocationDialogProps) => {
  const stepWrapperRef = useRef<HTMLElement>();
  const pinVerificationRef = useRef<PinVerificationRef>(null);
  const [isPinComplete, setIsPinComplete] = useState(false);
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const { selectedParcelLocker, verifyPinResponseData: lockers } = useSelector(
    (state: RootState) => state.bookingCreation
  );
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const step = useSelector((state: RootState) => state.bookingCreation.step);

  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );

  const { updateService } = useServiceStorage(
    "selectedService",
    ServiceTypeSelectorOption.PARCEL
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );

  const verificationPin = pinVerificationRef.current?.verificationPin;
  const fetchGroups = useFetchGroups("parcel");

  const createNewReservation = useCreateNewReservation();

  async function handlePinVerification() {
    if (step === BookingCreationStep.pinVerification) {
      // Handle PIN verification
      if (!verificationPin) {
        return;
      }

      try {
        setLoading(true);
        const result: any = await baseStore.dispatch(
          reservationsApiSlice.endpoints.verifyPin.initiate({
            verificationCode: verificationPin,
          })
        );

        if (result.data) {
          dispatch(
            setVerifyPinResponseData({
              ...(result.data as VerifyPinResponseData),
              verificationCode: verificationPin,
            })
          );
          dispatch(
            enqueueNotification({
              severity: "success",
              message: t(
                "book_location.parcel_placement.pin_verification.valid_code"
              ),
            })
          );
          dispatch(navigatedToNextStep());
        } else if (result.error) {
          dispatch(
            enqueueNotification({
              severity: "error",
              message: t(
                "book_location.parcel_placement.pin_verification.invalid_code"
              ),
            })
          );
        }
      } catch (error) {
        dispatch(
          enqueueNotification({
            severity: "error",
            message: t(
              "book_location.parcel_placement.pin_verification.error_occurred"
            ),
          })
        );
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(true);
      await createNewReservation();
      setLoading(false);
    }
  }

  const handleOpenLocker = async () => {
    if (!selectedParcelLocker) {
      return;
    }
    setLoading(true);
    const result: any = await baseStore.dispatch(
      reservationsApiSlice.endpoints.openLocker.initiate({
        assetId: selectedParcelLocker.asset,
        orderItemId: lockers?.orderItemId as string,
        verificationCode: lockers?.verificationCode ?? "",
      })
    );
    if (result.data) {
      if (lockers) {
        dispatch(
          setVerifyPinResponseData({
            ...lockers,
            deliveryToken: result.data.token,
          })
        );
      }
      dispatch(navigatedToNextStep());
    } else {
      dispatch(
        enqueueNotification({
          severity: "error",
          message: t("book_location.parcel_placement.unavailable_locker.title"),
        })
      );
    }
    setLoading(false);
  };
  // Check pin completion status
  useEffect(() => {
    if (
      step === BookingCreationStep.pinVerification &&
      pinVerificationRef.current
    ) {
      const checkPinComplete = () => {
        const isComplete = pinVerificationRef.current?.isPinComplete() || false;
        setIsPinComplete(isComplete);
      };

      // Check immediately
      checkPinComplete();

      // Set up an interval to check periodically
      const interval = setInterval(checkPinComplete, 100);

      return () => clearInterval(interval);
    }
  }, [step]);

  useEffect(() => {
    if (step === BookingCreationStep.lockSelection && lockers) {
      if (
        step === BookingCreationStep.lockSelection &&
        lockers?.groups?.length === 0
      ) {
        dispatch(setStep(BookingCreationStep.lockerUnavailability));
      }
    }
  }, [step, dispatch, lockers]);

  useEffect(() => {
    updateService(ServiceTypeSelectorOption.PARCEL);

    // dispatch(loadBookingFlow({}));
    stepWrapperRef.current = document.getElementById(
      "ParcelPlacementBookingProcessStepWrapper"
    )!;
    // @todo body scroll lock breaks the phone number input. Maybe remove it entirely?
    //disableBodyScroll(stepWrapperRef.current);
    //return () => enableBodyScroll(stepWrapperRef.current!);

    // Set the usecase to parcel for this flow
    dispatch(setUseCase({ useCase: "parcel" }));

    // Set the bookingFlow
    dispatch(
      bookingFlowChanged({
        bookingFlow: [
          "pinVerification",
          "lockSelection",
          "openLock",
          "success",
          "lockerUnavailability",
          "hints",
        ],
      })
    );
  }, []);

  useEffect(() => {
    const manageReduxData = async () => {
      // Handle different steps
      if (step === BookingCreationStep.lockSelection) {
        // Clean data
        dispatch(dateSelectedClear());
        dispatch(durationSelectedClear());
        setLoading(true);
        await fetchGroups();
        setLoading(false);
      }
      setLoading(false);
    };
    manageReduxData();
  }, [step]);

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.pinVerification:
        return t("book_location.parcel_placement.pin_verification.title");
      case BookingCreationStep.lockSelection:
        return t("book_location.parcel_placement.select_locker");
      case BookingCreationStep.openLock:
        return t("book_location.parcel_placement.locker_now_open");
      case BookingCreationStep.success:
        return t("book_location.parcel_placement.success.title").replace('[Quartiers-Hub]', tenantConfig.tenantServiceName?.[i18n.language] ?? 'Quartiers-Hub');
      case BookingCreationStep.lockerUnavailability:
        return t("book_location.parcel_placement.unavailable_locker.title");

      default:
        return "";
    }
  }, [step, orderData.date]);

  const closePage = () => {
    // history.goBack();
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const isLastStep =
    step === BookingCreationStep.success ||
    step === BookingCreationStep.lockerUnavailability;

  return (
    <Root>
      <Header>
        {step !== BookingCreationStep.success &&
          step !== BookingCreationStep.lockerUnavailability && (
            <div className="h-12 absolute left-2 flex items-center">
              <BookLocationNavigateBackAction />
            </div>
          )}
        <TenantLogo />
        {step !== BookingCreationStep.success &&
          step !== BookingCreationStep.lockerUnavailability && (
            <button
              id="closeButton"
              className="size-12 absolute right-0 text-foreground/90"
              onClick={() => setPageDialogOpen(true)}
            >
              <CloseIcon />
            </button>
          )}
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        <BookLocationHeader text={headerText} />
      </Box>

      <StepWrapper id={"parcelBookingProcessStepWrapper"}>
        {step === BookingCreationStep.pinVerification && (
          <PinVerification ref={pinVerificationRef} />
        )}
        {/* {step === BookingCreationStep.loading && <LoadingStep />} */}
        {step === BookingCreationStep.lockSelection && <LockerSelection />}
        {step === BookingCreationStep.openLock && <LockerOpen />}
        {step === BookingCreationStep.success && <SuccessMessage />}
        {step === BookingCreationStep.lockerUnavailability && (
          <NoLockerAvailable />
        )}
      </StepWrapper>
      <ActionsWrapper>
        {step === BookingCreationStep.pinVerification && !loading && (
          <CustomButton
            isButtonDark={true}
            variant={"outlined"}
            isOutlined={false}
            onClick={handlePinVerification}
            disabled={!isPinComplete}
            fullWidth={true}
            style={{ textTransform: "none" }}
            children={t(
              "book_location.parcel_placement.pin_verification.continue"
            )}
          />
        )}
        {step === BookingCreationStep.lockSelection && (
          <CustomButton
            isButtonDark={true}
            variant={"outlined"}
            isOutlined={false}
            onClick={handleOpenLocker}
            disabled={!selectedParcelLocker}
            fullWidth={true}
            style={{ textTransform: "none" }}
            children={t("book_location.parcel_placement.open_locker")}
          />
        )}
        {!isLastStep &&
          step !== BookingCreationStep.pinVerification &&
          step !== BookingCreationStep.lockSelection &&
          step !== BookingCreationStep.openLock &&
          step !== BookingCreationStep.success &&
          step !== BookingCreationStep.lockerUnavailability && (
            <BookLocationNavigateNextAction />
          )}
      </ActionsWrapper>

      {step !== BookingCreationStep.pinVerification && loading && (
        <LoaderOverlay open={loading} />
      )}
      {step === BookingCreationStep.pinVerification && loading && (
        <LoadingStep />
      )}

      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );
};

export default BookLocationPage;
